import { UUID } from 'crypto';
import { useRotationStore } from 'src/context/RotationContext';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';

export const useRotationKeyboard = () => {
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const { clearQueryParamsStowageBerthCall, setQueryParamStowageBerthCall, setQueryParamStowagePortCall } = useStowageQueryParamsUtils();

    const findPortCallIndex = (portCallGuid: UUID, filteredStowageRotationTree: RotationPortCall[]) => {
        return filteredStowageRotationTree.findIndex((x) => x.portCallGuid === portCallGuid);
    };

    const findBerthCallIndex = (portCall: RotationPortCall, berthCallGuid: UUID) => {
        return portCall.berthCalls.findIndex((x) => x.berthCallGuid === berthCallGuid);
    };

    const closePortCall = (
        portCallItem: RotationPortCall,
        openPortCallMenuItemGuids: UUID[],
        setOpenPortCallMenuItemGuids: (guids: UUID[]) => void
    ) => {
        setOpenPortCallMenuItemGuids([...openPortCallMenuItemGuids.filter((id) => id !== portCallItem.portCallGuid)]);
    };

    const openPortCalls = (
        portCallItem: RotationPortCall,
        openPortCallMenuItemGuids: UUID[],
        setOpenPortCallMenuItemGuids: (guids: UUID[]) => void
    ) => {
        if (!openPortCallMenuItemGuids.includes(portCallItem.portCallGuid)) {
            setOpenPortCallMenuItemGuids([...openPortCallMenuItemGuids, portCallItem.portCallGuid]);
        }
    };

    const handleArrowDown = (
        currentRotationTreeIndex: number,
        filteredStowageRotationTree: RotationPortCall[],
        openPortCallMenuItemGuids: UUID[],
        setOpenPortCallMenuItemGuids: (guids: UUID[]) => void
    ) => {
        const berthCalls = selectedStowageRotationPortCall.berthCalls;
        if (!selectedStowageRotationBerthCall) {
            if (berthCalls[0]) {
                setQueryParamStowageBerthCall(selectedStowageRotationPortCall.portCallGuid, berthCalls[0].berthCallGuid);
                openPortCalls(selectedStowageRotationPortCall, openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
            }
        } else {
            const currentBerthCallIndex = findBerthCallIndex(selectedStowageRotationPortCall, selectedStowageRotationBerthCall.berthCallGuid);
            if (currentBerthCallIndex === berthCalls.length - 1) {
                const nextPortCallItem = filteredStowageRotationTree[currentRotationTreeIndex + 1];
                nextPortCallItem && setQueryParamStowagePortCall(nextPortCallItem.portCallGuid);
            } else {
                setQueryParamStowageBerthCall(selectedStowageRotationPortCall.portCallGuid, berthCalls[currentBerthCallIndex + 1].berthCallGuid);
            }
        }
    };

    const handleArrowUp = (
        currentRotationTreeIndex: number,
        filteredStowageRotationTree: RotationPortCall[],
        openPortCallMenuItemGuids: UUID[],
        setOpenPortCallMenuItemGuids: (guids: UUID[]) => void
    ) => {
        if (!selectedStowageRotationBerthCall) {
            const previousPortCall = filteredStowageRotationTree[currentRotationTreeIndex - 1];
            if (previousPortCall) {
                if (previousPortCall.berthCalls[previousPortCall.berthCalls.length - 1]) {
                    setQueryParamStowageBerthCall(
                        previousPortCall.portCallGuid,
                        previousPortCall.berthCalls[previousPortCall.berthCalls.length - 1].berthCallGuid
                    );
                    openPortCalls(previousPortCall, openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
                } else {
                    setQueryParamStowagePortCall(previousPortCall.portCallGuid);
                }
            }
        } else {
            const currentBerthCallIndex = findBerthCallIndex(selectedStowageRotationPortCall, selectedStowageRotationBerthCall.berthCallGuid);
            if (currentBerthCallIndex === 0) {
                clearQueryParamsStowageBerthCall();
            } else {
                setQueryParamStowageBerthCall(
                    selectedStowageRotationPortCall.portCallGuid,
                    selectedStowageRotationPortCall.berthCalls[currentBerthCallIndex - 1].berthCallGuid
                );
            }
        }
    };

    const handleArrowRight = (openPortCallMenuItemGuids: UUID[], setOpenPortCallMenuItemGuids: (guids: UUID[]) => void) => {
        if (selectedStowageRotationPortCall?.berthCalls[0]) {
            setQueryParamStowageBerthCall(selectedStowageRotationPortCall.portCallGuid, selectedStowageRotationPortCall.berthCalls[0].berthCallGuid);
            openPortCalls(selectedStowageRotationPortCall, openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
        }
    };

    const handleArrowLeft = (openPortCallMenuItemGuids: UUID[], setOpenPortCallMenuItemGuids: (guids: UUID[]) => void) => {
        if (!selectedStowageRotationBerthCall) {
            closePortCall(selectedStowageRotationPortCall, openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
        } else {
            clearQueryParamsStowageBerthCall();
        }
    };

    const handleRotationArrowClicks = (
        event: React.KeyboardEvent<HTMLLIElement>,
        filteredStowageRotationTree: RotationPortCall[],
        openPortCallMenuItemGuids: UUID[],
        setOpenPortCallMenuItemGuids: (guids: UUID[]) => void
    ) => {
        if (!selectedStowageRotationPortCall) return;
        const currentRotationTreeIndex = findPortCallIndex(selectedStowageRotationPortCall?.portCallGuid, filteredStowageRotationTree);
        if (event.key === 'ArrowDown') {
            handleArrowDown(currentRotationTreeIndex, filteredStowageRotationTree, openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
        } else if (event.key === 'ArrowUp') {
            handleArrowUp(currentRotationTreeIndex, filteredStowageRotationTree, openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
        } else if (event.key === 'ArrowRight') {
            handleArrowRight(openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
        } else if (event.key === 'ArrowLeft') {
            handleArrowLeft(openPortCallMenuItemGuids, setOpenPortCallMenuItemGuids);
        }
    };

    return {
        handleRotationArrowClicks,
    };
};
